import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { fmtDate, prettyDate, money, brandCssVars } from "../lib/format";
import { providerLabel, providerLabelPlural, prepCopy } from "../lib/industry";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Check, ChevronRight, Clock, CheckCircle2, CalendarX, ClipboardList, Sparkles } from "lucide-react";

const COVER = "https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const buildSteps = (bt) => ["Service", "About you", providerLabel(bt), "Date & time", "Details"];

export default function PublicBooking() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const offerId = sp.get("offer");
  const [offerData, setOfferData] = useState(null);
  const [offerChecking, setOfferChecking] = useState(!!offerId);
  const [salon, setSalon] = useState(null);
  const [closed, setClosed] = useState(false);
  const [services, setServices] = useState([]);
  const [stylists, setStylists] = useState([]);
  const [deposit, setDeposit] = useState({ active: false, amount: 0 });
  const [welcome, setWelcome] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [loading, setLoading] = useState(true);

  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState("");
  const [stylistId, setStylistId] = useState("");
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slot, setSlot] = useState("");
  const [survey, setSurvey] = useState({ hair_type: "", hair_length: "", goals: "", allergies: "" });
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [waitOpen, setWaitOpen] = useState(false);
  const [waitBusy, setWaitBusy] = useState(false);
  const [waitWindow, setWaitWindow] = useState("any");

  const joinWaitlist = async () => {
    if (!contact.name || (!contact.email && !contact.phone)) { toast.error("Add your name and email or phone"); return; }
    setWaitBusy(true);
    try {
      await api.post("/public/waitlist", {
        slug, service_id: serviceId, stylist_id: stylistId || "any",
        client_name: contact.name, client_email: contact.email, client_phone: contact.phone,
        preferred_date: date ? fmtDate(date) : "", preferred_window: waitWindow,
      });
      toast.success("You're on the waitlist! We'll message you if a spot opens.");
      setWaitOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not join waitlist"); }
    finally { setWaitBusy(false); }
  };

  useEffect(() => {
    api.get(`/public/salon/${slug}`).then((r) => {
      if (r.data.closed) { setClosed(true); setSalon(r.data.salon); }
      else { setSalon(r.data.salon); setServices(r.data.services); setStylists(r.data.stylists); setDeposit(r.data.deposit);
             if (r.data.welcome) { setWelcome(r.data.welcome); if (!offerId) setShowWelcome(true); } }
    }).catch(() => setClosed(true)).finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!offerId) return;
    api.get(`/public/waitlist-offer/${offerId}`).then((r) => {
      const o = r.data;
      // prefill service / stylist / contact regardless, so a fallback wizard is partly ready
      if (o.service?.id) setServiceId(o.service.id);
      if (o.stylist?.id && o.stylist.id !== "any") setStylistId(o.stylist.id);
      setContact({ name: o.client?.name || "", email: o.client?.email || "", phone: o.client?.phone || "" });
      setDeposit(o.deposit || { active: false, amount: 0 });
      if (o.available && o.slot?.date && o.slot?.start_time) {
        setDate(new Date(`${o.slot.date}T00:00:00`));
        setSlot(o.slot.start_time);
        setOfferData(o);
      } else {
        if (o.status === "booked") toast.info("This spot was already booked. You can pick another time below.");
        else if (o.status === "expired") toast.info("This offer has expired. You can pick another time below.");
        else toast.info("This slot is no longer available. Pick another time below.");
      }
    }).catch(() => {}).finally(() => setOfferChecking(false));
  }, [offerId]);

  const service = useMemo(() => services.find((s) => s.id === serviceId), [services, serviceId]);
  const stylist = useMemo(() => stylists.find((s) => s.id === stylistId), [stylists, stylistId]);
  const bt = salon?.business_type;
  const plabel = providerLabel(bt);
  const steps = buildSteps(bt);
  const brand = salon?.branding?.color;
  const brandStyle = brand ? { backgroundColor: brand, borderColor: brand } : undefined;
  const brandVars = useMemo(() => brandCssVars(brand), [brand]);

  useEffect(() => {
    if (step === 3 && stylistId && date && serviceId) {
      setLoadingSlots(true); setSlot("");
      api.get("/public/availability", { params: { slug, stylist_id: stylistId, date: fmtDate(date), service_id: serviceId } })
        .then((r) => setSlots(r.data.slots)).catch(() => setSlots([])).finally(() => setLoadingSlots(false));
    }
  }, [step, stylistId, date, serviceId, slug]);

  const canNext = () => {
    if (step === 0) return !!serviceId;
    if (step === 1) return !!survey.hair_type;
    if (step === 2) return !!stylistId;
    if (step === 3) return !!date && !!slot;
    if (step === 4) return contact.name && (contact.email || contact.phone);
    return true;
  };

  const confirm = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/book", {
        slug, stylist_id: stylistId, service_id: serviceId, date: fmtDate(date), start_time: slot,
        client_name: contact.name, client_email: contact.email, client_phone: contact.phone,
        ...survey, origin_url: window.location.origin,
      });
      if (data.requires_payment) { window.location.href = data.checkout_url; return; }
      setDone(data.appointment);
      toast.success("Booked! Check your email for confirmation.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Booking failed"); }
    finally { setSubmitting(false); }
  };

  if (loading || offerChecking) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  if (closed) return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/40 px-6">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-4 text-muted-foreground"><CalendarX size={26} /></div>
        <h1 className="font-display text-2xl font-semibold">{salon?.name || "This salon"} isn't taking bookings right now</h1>
        <p className="text-muted-foreground mt-2">Please check back later or contact the salon directly.</p>
      </div>
    </div>
  );

  if (done) return (
    <div style={brandVars} className="min-h-screen bg-secondary/40">
      <Header salon={salon} />
      <div className="max-w-xl mx-auto px-6 py-16 text-center">
        <div className="w-16 h-16 rounded-full bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={34} /></div>
        <h1 className="font-display text-3xl font-semibold mb-2">You're booked!</h1>
        <p className="text-muted-foreground mb-8">A confirmation was sent to {done.client_email || "you"}.</p>
        <div className="bg-card border border-border rounded-lg p-6 text-left space-y-2">
          <Row label="Service" value={done.service_name} /><Row label={plabel} value={done.stylist_name} />
          <Row label="Date" value={prettyDate(done.date)} /><Row label="Time" value={`${done.start_time} – ${done.end_time}`} />
        </div>
      </div>
    </div>
  );

  if (offerData && offerData.available && !done) return (
    <div style={brandVars} className="min-h-screen bg-secondary/40">
      <Header salon={salon} />
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto mb-4"><Sparkles size={26} /></div>
          <h1 className="font-display text-3xl font-semibold">A spot just opened for you</h1>
          <p className="text-muted-foreground mt-2">Confirm in one tap to grab it before it's gone.</p>
        </div>
        {offerData.expires_label && (
          <div data-testid="offer-countdown" className="flex items-center justify-center gap-2 text-sm bg-accent/15 text-foreground rounded-md py-2.5 px-4 mb-5">
            <Clock size={15} className="text-accent" /> Held for you until <strong>{offerData.expires_label}</strong>
          </div>
        )}
        <div className="bg-card border border-border rounded-lg p-6 space-y-2.5">
          <Row label="Service" value={`${offerData.service?.name} · ${money(offerData.service?.price)}`} />
          <Row label={plabel} value={offerData.stylist?.name} />
          <Row label="When" value={`${prettyDate(offerData.slot?.date)} at ${offerData.slot?.start_time}`} />
          <Row label="Name" value={offerData.client?.name} />
          {offerData.deposit?.active && <Row label="Deposit due now" value={money(offerData.deposit.amount)} />}
        </div>
        <Button data-testid="offer-confirm" disabled={submitting} style={brandStyle}
          className="w-full mt-6 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md py-6 text-base"
          onClick={confirm}>
          {submitting ? "Please wait…" : offerData.deposit?.active ? `Pay ${money(offerData.deposit.amount)} deposit & confirm` : "Confirm this spot"}
        </Button>
        <button type="button" data-testid="offer-decline" onClick={() => setOfferData(null)}
          className="w-full text-center mt-3 text-sm text-muted-foreground hover:text-foreground underline underline-offset-4">
          Pick a different time instead
        </button>
      </div>
    </div>
  );

  if (welcome && showWelcome && !done && !(offerData && offerData.available)) {
    const gallery = salon?.gallery || [];
    return (
      <div style={brandVars} className="min-h-screen bg-secondary/40" data-testid="welcome-page">
        <Header salon={salon} />
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="text-center">
            <h1 className="font-display text-4xl font-semibold" data-testid="welcome-headline">{welcome.headline || `Welcome to ${salon?.name}`}</h1>
            {welcome.bio && <p className="text-muted-foreground mt-4 leading-relaxed whitespace-pre-line max-w-2xl mx-auto" data-testid="welcome-bio">{welcome.bio}</p>}
          </div>
          {gallery.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-10" data-testid="welcome-gallery">
              {gallery.slice(0, 6).map((g, i) => (
                <div key={(g.url || g) + i} className="aspect-square rounded-lg overflow-hidden border border-border">
                  <img src={g.url || g} alt={g.caption || "Salon"} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
          <div className="mt-10 flex justify-center">
            <Button data-testid="welcome-book-btn" style={brandStyle}
              className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8 py-6 text-base"
              onClick={() => setShowWelcome(false)}>
              Book an appointment <ChevronRight size={18} className="ml-1" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={brandVars} className="min-h-screen bg-secondary/40">
      <Header salon={salon} />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 mb-8 flex-wrap">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${i < step ? "bg-primary text-primary-foreground" : i === step ? "bg-primary text-primary-foreground" : "bg-card border border-border text-muted-foreground"}`}>{i < step ? <Check size={14} /> : i + 1}</div>
              <span className={`text-sm hidden sm:inline ${i === step ? "font-medium" : "text-muted-foreground"}`}>{s}</span>
              {i < steps.length - 1 && <ChevronRight size={14} className="text-muted-foreground/40" />}
            </div>
          ))}
        </div>

        <div className="bg-card border border-border rounded-lg p-8 min-h-[340px]">
          {step === 0 && (
            <div>
              <h2 className="font-display text-2xl font-semibold mb-1">Choose a service</h2>
              <p className="text-muted-foreground text-sm mb-6">What can we do for you today?</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {services.map((s) => (
                  <button key={s.id} data-testid={`pub-service-${s.id}`} onClick={() => setServiceId(s.id)}
                    className={`text-left p-4 rounded-lg border transition-colors ${serviceId === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                    <div className="flex justify-between items-start"><span className="font-medium">{s.name}</span><span className="font-display font-bold">{money(s.price)}</span></div>
                    <div className="text-xs text-muted-foreground mt-1">{s.duration_min} min · {s.category}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl font-semibold mb-1">Tell us about your hair</h2>
              <p className="text-muted-foreground text-sm mb-6">{prepCopy(bt)}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Hair type</Label>
                  <Select value={survey.hair_type} onValueChange={(v) => setSurvey((s) => ({ ...s, hair_type: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="pub-hair-type"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["Straight", "Wavy", "Curly", "Coily", "Fine", "Thick"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div><Label>Current length</Label>
                  <Select value={survey.hair_length} onValueChange={(v) => setSurvey((s) => ({ ...s, hair_length: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="pub-hair-length"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{["Short", "Chin", "Shoulder", "Mid-back", "Long"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
              <div className="mt-4"><Label>What look are you going for?</Label><Textarea data-testid="pub-goals" className="mt-1.5" rows={2} value={survey.goals} onChange={(e) => setSurvey((s) => ({ ...s, goals: e.target.value }))} /></div>
              <div className="mt-4"><Label>Allergies / sensitivities</Label><Input data-testid="pub-allergies" className="mt-1.5" value={survey.allergies} onChange={(e) => setSurvey((s) => ({ ...s, allergies: e.target.value }))} placeholder="None" /></div>
            </div>
          )}
          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl font-semibold mb-1">Pick your {plabel.toLowerCase()}</h2>
              <p className="text-muted-foreground text-sm mb-6">Choose who you'd like to see.</p>
              {stylists.length === 0 ? <p className="text-muted-foreground text-sm">No {providerLabelPlural(bt).toLowerCase()} available yet.</p> : (
                <div className="space-y-3">
                  {stylists.map((st) => (
                    <button key={st.id} data-testid={`pub-stylist-${st.id}`} onClick={() => setStylistId(st.id)}
                      className={`w-full flex items-center gap-4 p-4 rounded-lg border text-left transition-colors ${stylistId === st.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}>
                      {st.avatar
                        ? <img src={st.avatar} alt={st.name} className={`shrink-0 w-12 h-12 rounded-full object-cover transition-all ${stylistId === st.id ? "ring-2 ring-primary ring-offset-2 ring-offset-card" : "ring-1 ring-border"}`} />
                        : <div className={`shrink-0 w-12 h-12 rounded-full bg-secondary flex items-center justify-center font-display font-bold text-muted-foreground transition-all ${stylistId === st.id ? "ring-2 ring-primary ring-offset-2 ring-offset-card" : "ring-1 ring-border"}`}>{(st.name || "?")[0]}</div>}
                      <div><div className="font-medium">{st.name}</div><div className="text-sm text-muted-foreground">{st.bio || (st.specialties || []).join(", ")}</div></div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl font-semibold mb-1">Choose date & time</h2>
              <p className="text-muted-foreground text-sm mb-6">Live availability for {stylist?.name}.</p>
              <div className="grid md:grid-cols-2 gap-8">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={{ before: new Date() }} className="rounded-md border border-border" data-testid="pub-calendar" />
                <div>
                  <div className="text-sm text-muted-foreground mb-3">{date ? prettyDate(fmtDate(date)) : "Select a date"}</div>
                  {loadingSlots ? <div className="text-muted-foreground text-sm">Loading…</div> : date && slots.length === 0 ? <div className="text-muted-foreground text-sm">No availability. Try another day.</div> : (
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map((t) => (
                        <button key={t} data-testid={`pub-slot-${t}`} onClick={() => setSlot(t)}
                          className={`flex items-center justify-center gap-1 py-2 rounded-md border text-sm transition-colors ${slot === t ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}>
                          <Clock size={12} /> {t}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl font-semibold mb-1">Your details</h2>
              <p className="text-muted-foreground text-sm mb-6">We'll send your confirmation here.</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="sm:col-span-2"><Label>Full name</Label><Input data-testid="pub-name" className="mt-1.5" value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} /></div>
                <div><Label>Email</Label><Input data-testid="pub-email" type="email" className="mt-1.5" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} /></div>
                <div><Label>Mobile</Label><Input data-testid="pub-phone" className="mt-1.5" value={contact.phone} onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} placeholder="+1 555 000 0000" /></div>
              </div>
              <div className="bg-secondary/60 rounded-lg p-5 space-y-2">
                <Row label="Service" value={`${service?.name} · ${money(service?.price)}`} />
                <Row label={plabel} value={stylist?.name} />
                <Row label="When" value={`${date ? prettyDate(fmtDate(date)) : ""} at ${slot}`} />
                {deposit.active && <Row label="Deposit due now" value={money(deposit.amount)} />}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between items-center mt-6">
          <Button variant="ghost" data-testid="pub-back" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>Back</Button>
          <div className="flex items-center gap-3">
            {serviceId && step >= 2 && (
              <button type="button" data-testid="open-waitlist" onClick={() => setWaitOpen(true)} className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4">
                Join the waitlist instead
              </button>
            )}
            {step < 4 ? (
              <Button data-testid="pub-next" disabled={!canNext()} style={brandStyle} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8" onClick={() => setStep((s) => s + 1)}>Continue <ChevronRight size={16} className="ml-1" /></Button>
            ) : (
              <Button data-testid="pub-confirm" disabled={!canNext() || submitting} style={brandStyle} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-8" onClick={confirm}>
                {submitting ? "Please wait…" : deposit.active ? `Pay ${money(deposit.amount)} deposit & book` : "Confirm booking"}
              </Button>
            )}
          </div>
        </div>

        {step === 0 && (salon?.gallery?.length > 0) && (
          <div className="mt-10" data-testid="booking-gallery">
            <h2 className="font-display text-2xl font-semibold mb-1">Our work</h2>
            <p className="text-sm text-muted-foreground mb-4">A look at recent transformations.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {salon.gallery.map((g, i) => {
                const url = g.url || g;
                const caption = g.caption || "";
                return (
                  <figure key={i} className="group">
                    <div className="aspect-square rounded-lg overflow-hidden border border-border">
                      <img src={url} alt={caption || "Salon work"} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                    {caption && <figcaption className="text-xs text-muted-foreground mt-1.5 text-center">{caption}</figcaption>}
                  </figure>
                );
              })}
            </div>
          </div>
        )}

        <Dialog open={waitOpen} onOpenChange={setWaitOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-xl font-semibold flex items-center gap-2"><ClipboardList size={18} /> Join the waitlist</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground -mt-2">We'll text or email you the moment a matching spot opens up{service ? ` for ${service.name}` : ""}{date ? ` around ${prettyDate(fmtDate(date))}` : ""}.</p>
            <div className="space-y-3 mt-2">
              <div><Label>Full name</Label><Input data-testid="wait-name" className="mt-1" value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input data-testid="wait-email" type="email" className="mt-1" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} /></div>
                <div><Label>Mobile</Label><Input data-testid="wait-phone" className="mt-1" value={contact.phone} onChange={(e) => setContact((c) => ({ ...c, phone: e.target.value }))} /></div>
              </div>
              <div>
                <Label>Preferred time</Label>
                <Select value={waitWindow} onValueChange={setWaitWindow}>
                  <SelectTrigger className="mt-1" data-testid="wait-window"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any time</SelectItem>
                    <SelectItem value="morning">Mornings (before 12pm)</SelectItem>
                    <SelectItem value="afternoon">Afternoons (12–5pm)</SelectItem>
                    <SelectItem value="evening">Evenings (after 5pm)</SelectItem>
                    <SelectItem value="weekday">Weekdays</SelectItem>
                    <SelectItem value="weekend">Weekends</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button data-testid="wait-submit" disabled={waitBusy} style={brandStyle} className="bg-primary text-primary-foreground hover:bg-primary/90" onClick={joinWaitlist}>{waitBusy ? "Joining…" : "Join waitlist"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

function Header({ salon }) {
  const br = salon?.branding || {};
  const cover = br.cover || COVER;
  return (
    <div className="relative h-52 overflow-hidden">
      <img src={cover} alt="Salon" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative max-w-3xl mx-auto px-6 h-full flex flex-col justify-end pb-6">
        <div className="flex items-center gap-3">
          {br.logo && <img src={br.logo} alt="logo" className="w-12 h-12 rounded-lg object-cover bg-white" />}
          <div>
            <h1 className="font-display text-3xl font-semibold text-white">{salon?.name}</h1>
            {salon?.tagline && <p className="text-white/80 text-sm mt-1">{salon.tagline}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
function Row({ label, value }) {
  return <div className="flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{value}</span></div>;
}
